import {
  collection, getDocs, getDoc, doc, setDoc, deleteDoc, writeBatch,
  query, where, limit, onSnapshot, documentId, updateDoc, Timestamp,
  DocumentData, QueryDocumentSnapshot, DocumentSnapshot, Unsubscribe, deleteField
} from 'firebase/firestore';
import { vacancyGaps, openTimeOf, DEFAULT_MIN_SHIFT_MINUTES } from './utils/coverage';
import { isoAt, rangeLabel, addDaysStr } from './utils/shiftTime';
import { sendPasswordResetEmail, signInWithEmailAndPassword, signInWithCustomToken, signOut as fbSignOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from './firebase';
import { C } from './collections';
import {
  Store, Staff, Shift, ShiftType, Position, DailyEvent, Attendance, Absence, Duty, StaffRule,
  ShiftPattern, FirestoreDocData, SendInviteEmailData, LinkLineAccountData,
  ShiftSosData, ShiftSosResolvedData, DefaultShiftSetting
} from './types';
import { getJSTDateString, timeToMinutes } from './utils/timeUtils';
import { GoogleGenAI, Type } from "@google/genai";

export * from './types';
export { getJSTDate, getJSTDateString, timeToMinutes, minutesToTime, calculateLateNightMinutes, createJSTDate, calculateDurationMinutes } from './utils/timeUtils';

// Firestoreドキュメントから型付きオブジェクトへの変換ヘルパー
const docToTyped = <T extends { id: string }>(d: QueryDocumentSnapshot<DocumentData>): T => {
  return { ...d.data(), id: d.id } as T;
};

// ---- 通知の送信結果（画面のトースト表示用） ----
export type NotificationChannel = 'LINE' | 'メール';
export type MockNotification = { at: string; channel: NotificationChannel; title: string; detail?: string };
const notificationListeners = new Set<(n: MockNotification) => void>();
export const onMockNotification = (fn: (n: MockNotification) => void): Unsubscribe => {
  notificationListeners.add(fn);
  return () => { notificationListeners.delete(fn); };
};
const pushNotification = (n: Omit<MockNotification, 'at'>) => {
  const item = { ...n, at: new Date().toISOString() };
  notificationListeners.forEach(fn => fn(item));
};

// 店舗がLINEを使う運用かどうか。未設定は従来どおりLINE利用とみなす。
export const isLineEnabled = (store?: Pick<Store, 'lineEnabled'> | null): boolean => store?.lineEnabled !== false;
export const getNotificationChannelLabel = (store?: Pick<Store, 'lineEnabled'> | null): string =>
  isLineEnabled(store) ? 'LINE' : 'メール・アプリ内のお知らせ';

// Cloud Functions の通知（LINE／メール）を呼び出し、送信結果を画面に表示する。
type NotifyResult = { line: number; mail: number };
const callNotify = async (payload: { storeId: string; audience: 'store_staff' | 'managers' | 'list'; staffIds?: string[]; excludeStaffIds?: string[]; title: string; body: string; toastTitle: string; toastDetail: string }) => {
  const { toastTitle, toastDetail, ...data } = payload;
  const res = await httpsCallable<typeof data, NotifyResult>(functions, 'notifyStaff')(data);
  const { line, mail } = res.data || { line: 0, mail: 0 };
  const parts = [line > 0 ? `LINE ${line}名` : '', mail > 0 ? `メール ${mail}名` : ''].filter(Boolean).join('、') || '送信対象なし';
  pushNotification({ channel: line > 0 ? 'LINE' : 'メール', title: toastTitle, detail: `${toastDetail}、${parts}` });
  return res.data;
};
const mdOf = (iso: string) => `${Number(iso.slice(5, 7))}/${Number(iso.slice(8, 10))}`;

const docToTypedSnap = <T extends { id: string }>(d: DocumentSnapshot<DocumentData>): T => ({ ...(d.data() as DocumentData), id: d.id } as T);

const chunkArray = <T>(array: T[], size: number): T[][] => {
  const chunked: T[][] = [];
  for (let i = 0; i < array.length; i += size) chunked.push(array.slice(i, i + size));
  return chunked;
};

// undefinedフィールドを除去してFirestoreに保存可能なオブジェクトを返す
const cleanData = <T extends object>(data: T): Partial<T> => {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }
  return cleaned as Partial<T>;
};

// Firestoreの日付フィールド（Timestamp | string | Date）をDateに変換
const ensureDate = (dateVal: string | Date | { toDate: () => Date } | null | undefined): Date => {
  if (!dateVal) return new Date();
  if (dateVal instanceof Timestamp) return dateVal.toDate();
  if (typeof dateVal === 'object' && 'toDate' in dateVal && typeof dateVal.toDate === 'function') return dateVal.toDate();
  const d = new Date(dateVal as string);
  return isNaN(d.getTime()) ? new Date() : d;
};

export const getTrialStatus = (store: Store) => {
  const TRIAL_TOTAL_DAYS = 60;
  const createdDate = ensureDate(store.createdAt);
  const now = new Date();
  const expiryDate = new Date(createdDate.getTime() + TRIAL_TOTAL_DAYS * 24 * 60 * 60 * 1000);
  const remainingMs = expiryDate.getTime() - now.getTime();
  const daysRemaining = Math.max(0, Math.ceil(remainingMs / (1000 * 60 * 60 * 24)));
  return { daysRemaining, isExpired: remainingMs <= 0, isPaid: !!store.stripeSubscriptionId, expiryDate };
};

// Staffドキュメントのデフォルト値を補完
const normalizeStaffDoc = (d: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>): Staff => {
  const data = d.data() as DocumentData;
  return {
    ...data,
    id: d.id,
    skills: data.skills || [],
    positionIds: data.positionIds || [],
    stores: data.stores || [],
  } as Staff;
};

const tenantApi = {
  getStores: async (): Promise<Store[]> => {
    const user = auth.currentUser;
    if (!user) return [];
    const ownedSnap = await getDocs(query(collection(db, C.stores), where('ownerId', '==', user.uid)));
    const ownedStores = ownedSnap.docs.map(d => docToTyped<Store>(d));
    const staffDoc = await getDoc(doc(db, C.staff, user.uid));
    const staffData = staffDoc.exists() ? normalizeStaffDoc(staffDoc) : null;
    const storeIds = (staffData?.stores ?? []).filter(id => !ownedStores.find(s => s.id === id));
    let otherStores: Store[] = [];
    if (storeIds.length > 0) {
      const idChunks = chunkArray(storeIds, 30);
      const snaps = await Promise.all(idChunks.map(chunk => getDocs(query(collection(db, C.stores), where(documentId(), 'in', chunk)))));
      otherStores = snaps.flatMap(s => s.docs.map(d => docToTyped<Store>(d)));
    }
    return [...ownedStores, ...otherStores].sort((a, b) => ensureDate(a.createdAt).getTime() - ensureDate(b.createdAt).getTime());
  },

  getStaff: async (): Promise<Staff[]> => {
    const user = auth.currentUser;
    if (!user) return [];
    const stores = await tenantApi.getStores();
    const storeIds = stores.map(s => s.id);
    if (storeIds.length === 0) return [];
    const staffMap = new Map<string, Staff>();
    const idChunks = chunkArray(storeIds, 10);
    const snapshots = await Promise.all(idChunks.map(chunk => getDocs(query(collection(db, C.staff), where('stores', 'array-contains-any', chunk)))));
    snapshots.forEach(snap => snap.docs.forEach(d => {
      staffMap.set(d.id, normalizeStaffDoc(d));
    }));
    return Array.from(staffMap.values());
  },

  getShifts: async (period?: { start: string; end: string }, storeId?: string): Promise<Shift[]> => {
    if (!storeId) return [];
    let q = query(collection(db, C.shifts), where('store', '==', storeId));
    if (period) { q = query(q, where('start', '>=', `${period.start}T00:00:00`), where('start', '<=', `${period.end}T23:59:59`)); }
    const snap = await getDocs(q);
    return snap.docs.map(d => docToTyped<Shift>(d)).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  },

  // storeIds（配列）またはstoreId（単一文字列）の両方のデータ形式に対応
  getShiftTypes: async (): Promise<ShiftType[]> => {
    const user = auth.currentUser;
    if (!user) return [];

    const snap = await getDocs(collection(db, C.shift_types));
    const allTypes = snap.docs.map(d => ({ ...d.data(), id: parseInt(d.id, 10) || Number(d.id) } as ShiftType));

    const stores = await tenantApi.getStores();
    const myStoreIds = new Set(stores.map(s => s.id));

    return allTypes.filter(t => {
      if (t.storeIds && Array.isArray(t.storeIds)) {
        return t.storeIds.some(id => myStoreIds.has(id));
      }
      if (t.storeId) {
        return myStoreIds.has(t.storeId);
      }
      return false;
    });
  },

  getPositions: async (): Promise<Position[]> => {
    const stores = await tenantApi.getStores();
    const storeIds = stores.map(s => s.id);
    if (storeIds.length === 0) return [];
    const snap = await getDocs(query(collection(db, C.positions), where('storeIds', 'array-contains-any', storeIds)));
    return snap.docs.map(d => docToTyped<Position>(d));
  },

  getShiftPatterns: async (storeId: string): Promise<ShiftPattern[]> => {
    const snap = await getDocs(query(collection(db, C.shift_patterns), where('storeId', '==', storeId)));
    return snap.docs.map(d => docToTyped<ShiftPattern>(d));
  },

  saveShiftPattern: async (pattern: ShiftPattern): Promise<void> => {
    await setDoc(doc(db, C.shift_patterns, pattern.id), cleanData(pattern), { merge: true });
  },

  deleteShiftPattern: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, C.shift_patterns, id));
  },

  listenToStaffProfile: (staffId: string, callback: (staff: Staff | null) => void): Unsubscribe => {
    if (!staffId) return () => {};
    return onSnapshot(doc(db, C.staff, staffId), (d) => {
      if (d.exists()) {
        callback(normalizeStaffDoc(d));
      } else {
        callback(null);
      }
    });
  },

  saveStores: async (stores: Store[]): Promise<Store[]> => {
    const batch = writeBatch(db);
    stores.forEach(s => batch.set(doc(db, C.stores, s.id), cleanData(s), { merge: true }));
    await batch.commit();
    return stores;
  },

  updateStoreLock: async (storeId: string, isLocked: boolean): Promise<void> => {
    await updateDoc(doc(db, C.stores, storeId), { isLocked });
  },

  deleteStore: async (id: string): Promise<void> => { await deleteDoc(doc(db, C.stores, id)); },

  deleteStores: async (ids: string[]): Promise<void> => {
    const batch = writeBatch(db);
    ids.forEach(id => batch.delete(doc(db, C.stores, id)));
    await batch.commit();
  },

  updateCompanyName: async (newName: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const storesSnap = await getDocs(query(collection(db, C.stores), where('ownerId', '==', user.uid)));
    const batch = writeBatch(db);
    storesSnap.docs.forEach((d) => batch.update(d.ref, { companyName: newName }));
    await batch.commit();
  },

  // スタッフ情報の保存。LINE連携・ログインアカウント・招待の状態は招待／連携の処理だけが更新するため、
  // 既存のスタッフでは編集画面の値で上書きしない。
  saveStaff: async (staffList: Staff[]): Promise<Staff[]> => {
    const existing = await Promise.all(staffList.map(s => getDoc(doc(db, C.staff, s.id))));
    const batch = writeBatch(db);
    staffList.forEach((s, i) => {
      const cleaned = cleanData(s) as Record<string, unknown>;
      if (existing[i].exists()) { delete cleaned.lineStatus; delete cleaned.lineUserId; delete cleaned.accountStatus; delete cleaned.inviteToken; delete cleaned.inviteTokenHash; }
      if (!cleaned.skills) cleaned.skills = [];
      if (!cleaned.positionIds) cleaned.positionIds = [];
      batch.set(doc(db, C.staff, s.id), cleaned, { merge: true });
    });
    await batch.commit();
    return staffList;
  },

  deleteStaff: async (staffId: string): Promise<void> => { await deleteDoc(doc(db, C.staff, staffId)); },

  saveShifts: async (shifts: Shift[]): Promise<void> => {
    const chunks = chunkArray(shifts, 450);
    for (const chunk of chunks) {
      const batch = writeBatch(db);
      chunk.forEach(s => batch.set(doc(db, C.shifts, s.id), cleanData(s), { merge: true }));
      await batch.commit();
    }
  },

  deleteShift: async (shiftId: string, recreateAsVacancy: boolean = false): Promise<void> => {
    if (recreateAsVacancy) {
      const snap = await getDoc(doc(db, C.shifts, shiftId));
      if (snap.exists()) {
        const data = snap.data() as FirestoreDocData<Shift>;
        if (data.confirmed || data.type === 'fixed') {
          // 同じ時間帯・ポジションの募集枠がある場合は、その枠の不足として扱うので新しく作らない。
          const day = data.start.slice(0, 10);
          const sameDay = await getDocs(query(collection(db, C.shifts), where('store', '==', data.store), where('start', '>=', `${day}T00:00:00`), where('start', '<=', `${day}T23:59:59`)));
          // 欠員枠を埋めていたシフトを外した場合は、その欠員枠がまた不足になるので新しく作らない
          const covered = !!data.vacancyId || sameDay.docs.map(d => d.data() as Shift).some(v => v.type === 'vacancy' && v.label !== '欠員' && v.positionId === data.positionId && v.start < data.end && v.end > data.start);
          if (!covered) {
            const vacancy: Shift = { id: `vac_${Date.now()}`, staffId: null, store: data.store, start: data.start, end: data.end, positionId: data.positionId, type: 'vacancy', confirmed: false, headcount: 1, label: '欠員' };
            await setDoc(doc(db, C.shifts, vacancy.id), cleanData(vacancy));
          }
        }
      }
    }
    await deleteDoc(doc(db, C.shifts, shiftId));
  },

  cleanupStaffStoreData: async (staffId: string, storeId: string): Promise<void> => {
    const today = getJSTDateString(new Date());
    const shiftsQuery = query(collection(db, C.shifts), where('staffId', '==', staffId), where('store', '==', storeId), where('start', '>=', `${today}T00:00:00`));
    const snap = await getDocs(shiftsQuery);
    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
  },

  publishShifts: async (data: { storeId: string; period: { start: string; end: string } }) => {
    await callNotify({ storeId: data.storeId, audience: 'store_staff', title: 'シフト募集開始', body: `新しいシフト（${data.period.start}〜${data.period.end}）の募集が始まりました。アプリから希望を提出してください。`, toastTitle: 'シフト提出依頼を送信しました。', toastDetail: `対象期間 ${mdOf(data.period.start)}〜${mdOf(data.period.end)}` });
    return { data: { ok: true } };
  },

  confirmShifts: async (storeId: string, period: { start: string; end: string }): Promise<void> => {
    // 期間内の割り当て済みシフトを全て confirmed: true に更新
    const shiftsQuery = query(
      collection(db, C.shifts),
      where('store', '==', storeId),
      where('start', '>=', `${period.start}T00:00:00`),
      where('start', '<=', `${period.end}T23:59:59`)
    );
    const snap = await getDocs(shiftsQuery);
    const batch = writeBatch(db);
    // 確定対象は、調整で割り当て済み（confirmed）のシフトと固定シフトのみ。割り当てられなかった希望・休み希望は確定しない。
    const staffIds = new Set<string>();
    snap.docs.forEach(d => {
      const data = d.data();
      if (data.staffId && data.type !== 'vacancy' && data.type !== 'off' && (data.confirmed || data.type === 'fixed')) {
        batch.update(d.ref, { confirmed: true });
        staffIds.add(data.staffId);
      }
    });
    await batch.commit();
    await updateDoc(doc(db, C.stores, storeId), { shiftCreationStatus: 'published' });
    await callNotify({ storeId, audience: 'list', staffIds: [...staffIds], title: 'シフト確定', body: `${period.start}〜${period.end} のシフトが確定しました。アプリで確認してください。`, toastTitle: 'シフト確定通知を送信しました。', toastDetail: `確定 ${staffIds.size}名分` });
  },

  // スタッフの欠勤申請（サーバーで本人のシフトか確認し、欠員枠の作成とSOS・管理者への連絡を行う）
  reportAbsence: async (shiftId: string): Promise<void> => {
    const r = await httpsCallable<{ shiftId: string }, { line: number; mail: number }>(functions, 'reportAbsence')({ shiftId });
    pushNotification({ channel: r.data.line > 0 ? 'LINE' : 'メール', title: '欠勤を申請しました。', detail: '管理者に連絡し、店舗のスタッフに助っ人募集（SOS）を送りました。' });
  },
  sendSosNotification: async (data: ShiftSosData) => {
    const isAbsence = data.messageType === 'automatic_absence';
    const body = isAbsence ? `${data.metadata?.absentStaffName}さんがお休みになったため、欠員が出ました。出勤できる方はアプリから応募してください。` : '人手が足りない時間帯があります。出勤できる方はアプリで詳細を確認してください。';
    await callNotify({ storeId: data.storeId, audience: 'store_staff', excludeStaffIds: data.metadata?.absentStaffId ? [data.metadata.absentStaffId] : undefined, title: 'SOS・急募', body, toastTitle: '助っ人募集（SOS）を送信しました。', toastDetail: data.metadata?.absentStaffName ? `${data.metadata.absentStaffName}さんの欠勤分` : '人手が足りない時間帯の募集' });
    if (isAbsence) await callNotify({ storeId: data.storeId, audience: 'managers', title: '欠勤の連絡', body: `${data.metadata?.absentStaffName}さんから欠勤の申請がありました。スタッフ全員にSOSを送っています。`, toastTitle: '管理者に欠勤を通知しました。', toastDetail: data.metadata?.absentStaffName || '' });
    return { data: { ok: true } };
  },

  sendSosResolvedNotification: async (data: ShiftSosResolvedData) => {
    await callNotify({ storeId: data.storeId, audience: 'managers', title: 'SOS解決', body: `欠員枠に ${data.assignedStaffName} さんが入りました。募集を停止しました。`, toastTitle: '助っ人が決まりました。', toastDetail: `${data.assignedStaffName}さん` });
    return { data: { ok: true } };
  },

  // 希望が未提出のスタッフへの催促
  sendShiftReminder: async (storeId: string, staffIds: string[]): Promise<void> => {
    await callNotify({ storeId, audience: 'list', staffIds, title: 'シフト希望の提出のお願い', body: 'シフト希望がまだ提出されていません。アプリから提出をお願いします。', toastTitle: 'シフト希望の提出を催促しました。', toastDetail: `${staffIds.length}名` });
  },

  // 確定済みシフトの変更（時間の変更・交代・追加）をスタッフに通知する
  notifyShiftChange: async (staffId: string, title: string, detail: string): Promise<void> => {
    const st = await getDoc(doc(db, C.staff, staffId));
    const storeId = st.exists() ? ((st.data().stores || [])[0] || '') : '';
    await callNotify({ storeId, audience: 'list', staffIds: [staffId], title: 'シフトの変更', body: `${title.replace(/を通知しました。$/, '')}があります。${detail}。アプリで確認してください。`, toastTitle: title, toastDetail: `${st.exists() ? st.data().lastName : ''}さん宛、${detail}` });
  },

  // ---- 人手が足りない枠の追加募集・打診・応募 ----
  openCallVacancies: async (storeId: string, vacancyIds: string[]): Promise<number> => {
    const batch = writeBatch(db);
    vacancyIds.forEach(id => batch.update(doc(db, C.shifts, id), { openCall: true }));
    await batch.commit();
    const res = await callNotify({ storeId, audience: 'store_staff', title: '追加募集', body: '人手が足りないシフトがあります。入れる方はアプリの「追加募集・打診」から応募してください。', toastTitle: '追加募集を送信しました。', toastDetail: `不足している${vacancyIds.length}枠` });
    return (res?.line || 0) + (res?.mail || 0);
  },

  offerShift: async (vacancyId: string, staffId: string): Promise<void> => {
    const vSnap = await getDoc(doc(db, C.shifts, vacancyId));
    if (!vSnap.exists()) throw new Error('対象が見つかりません。');
    const v = vSnap.data() as Shift;
    await updateDoc(vSnap.ref, { offeredTo: Array.from(new Set([...(v.offeredTo || []), staffId])) });
    const st = await getDoc(doc(db, C.staff, staffId));
    await callNotify({ storeId: v.store, audience: 'list', staffIds: [staffId], title: 'シフトの打診', body: `${mdOf(v.start)} ${rangeLabel(v)} に入れるか確認させてください。入れる場合はアプリの「追加募集・打診」から応募してください。`, toastTitle: 'シフトの打診を送信しました。', toastDetail: `${st.exists() ? st.data().lastName : ''}さん宛、${mdOf(v.start)} ${v.start.slice(11, 16)}〜${v.end.slice(11, 16)}` });
  },

  // スタッフからの応募（Cloud Functionsで応募者に追加し、管理者に通知する）
  applyForOpenShift: async (vacancyId: string, _staffId: string): Promise<void> => {
    await httpsCallable(functions, 'applyForOpenShift')({ vacancyId });
    pushNotification({ channel: 'LINE', title: '追加募集に応募しました。', detail: '管理者へ通知しました' });
  },

  getOpenShiftsForStaff: async (staffId: string): Promise<Array<{ vacancy: Shift; store: Store | null; offered: boolean; applied: boolean }>> => {
    const meSnap = await getDoc(doc(db, C.staff, staffId));
    if (!meSnap.exists()) return [];
    const me = normalizeStaffDoc(meSnap);
    const today = getJSTDateString(new Date());
    const storeIds = me.stores.slice(0, 30);
    if (storeIds.length === 0) return [];
    const [vacSnap, mineSnap] = await Promise.all([
      getDocs(query(collection(db, C.shifts), where('store', 'in', storeIds), where('start', '>=', `${today}T00:00:00`))),
      getDocs(query(collection(db, C.shifts), where('staffId', '==', staffId))),
    ]);
    const all = vacSnap.docs.map(d => docToTyped<Shift>(d));
    const busyDays = new Set(mineSnap.docs.map(d => d.data() as Shift).filter(x => x.confirmed || x.type === 'fixed').map(x => x.start.slice(0, 10)));
    const nowStr = `${today}T${new Date().toTimeString().slice(0, 5)}:00`;
    const stores = await tenantApi.getStores();
    // 人数が足りている枠は表示しない。足りない枠は、管理者が割り当てるときと同じ時間（足りない時間帯・最短勤務時間を考慮）で表示する
    const vacancies = all.filter(x => x.type === 'vacancy');
    const assignedAll = all.filter(x => x.staffId && (x.confirmed || x.type === 'fixed'));
    return all.filter(v => v.type === 'vacancy')
      .map(v => {
        const t = openTimeOf(v, vacancyGaps(v, vacancies, assignedAll), stores.find(s => s.id === v.store)?.minShiftMinutes || DEFAULT_MIN_SHIFT_MINUTES);
        return t ? { ...v, start: isoAt(v.start.slice(0, 10), t.start), end: isoAt(v.start.slice(0, 10), t.end) } : null;
      })
      .filter((v): v is Shift => !!v && v.end > nowStr)
      .filter(v => (v.offeredTo || []).includes(staffId) || (v.openCall && (me.positionIds || []).includes(v.positionId || '')))
      .filter(v => !busyDays.has(v.start.slice(0, 10)))
      .sort((a, b) => a.start.localeCompare(b.start))
      .map(v => ({ vacancy: v, store: stores.find(s => s.id === v.store) || null, offered: (v.offeredTo || []).includes(staffId), applied: (v.applicants || []).includes(staffId) }));
  },

  // デフォルトシフト設定の保存
  saveDefaultSettings: async (settings: DefaultShiftSetting[]): Promise<void> => {
    const batch = writeBatch(db);
    settings.forEach(s => {
      batch.set(doc(db, C.default_shift_settings, s.id), cleanData(s), { merge: true });
    });
    await batch.commit();
  },

  // デフォルトシフト設定の取得
  getDefaultSettings: async (staffId: string, storeId: string): Promise<DefaultShiftSetting[]> => {
    const q = query(
      collection(db, C.default_shift_settings),
      where('staffId', '==', staffId),
      where('storeId', '==', storeId)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as DefaultShiftSetting));
  },

  savePositions: async (positions: Position[]): Promise<void> => {
    const batch = writeBatch(db);
    positions.forEach(p => batch.set(doc(db, C.positions, p.id), cleanData(p), { merge: true }));
    await batch.commit();
  },

  deletePosition: async (id: string): Promise<void> => { await deleteDoc(doc(db, C.positions, id)); },

  saveShiftTypes: async (shiftTypes: ShiftType[]): Promise<void> => {
    const batch = writeBatch(db);
    shiftTypes.forEach(st => batch.set(doc(db, C.shift_types, String(st.id)), cleanData(st), { merge: true }));
    await batch.commit();
  },

  deleteShiftType: async (id: number): Promise<void> => { await deleteDoc(doc(db, C.shift_types, String(id))); },

  getAbsences: async (storeId: string): Promise<Absence[]> => {
    const snap = await getDocs(query(collection(db, C.absences), where('storeId', '==', storeId)));
    return snap.docs.map(d => docToTyped<Absence>(d));
  },

  getEvents: async (storeId: string): Promise<DailyEvent[]> => {
    const snap = await getDocs(query(collection(db, C.events), where('storeId', '==', storeId)));
    return snap.docs.map(d => docToTyped<DailyEvent>(d));
  },

  saveEvent: (event: DailyEvent) => setDoc(doc(db, C.events, event.id), cleanData(event), { merge: true }),
  deleteEvent: (eventId: string) => deleteDoc(doc(db, C.events, eventId)),

  subscribeShifts: (staffId: string, callback: (shifts: Shift[]) => void): Unsubscribe => {
    return onSnapshot(query(collection(db, C.shifts), where('staffId', '==', staffId)), (snap) =>
      callback(snap.docs.map(d => docToTyped<Shift>(d)))
    );
  },

  getAttendance: async (storeId: string): Promise<Attendance[]> => {
    if (!storeId) return [];
    const snap = await getDocs(query(collection(db, C.attendance), where('storeId', '==', storeId)));
    return snap.docs.map(d => docToTyped<Attendance>(d)).sort((a, b) => b.date.localeCompare(a.date));
  },

  getTodayAttendance: async (staffId: string, date: string): Promise<Attendance | null> => {
    const snap = await getDocs(query(collection(db, C.attendance), where('staffId', '==', staffId), where('date', '==', date), limit(1)));
    return snap.empty ? null : docToTyped<Attendance>(snap.docs[0]);
  },

  getUnfinishedAttendance: async (staffId: string): Promise<Attendance | null> => {
    const snap = await getDocs(query(collection(db, C.attendance), where('staffId', '==', staffId), where('status', 'in', ['working', 'break']), limit(1)));
    return snap.empty ? null : docToTyped<Attendance>(snap.docs[0]);
  },

  saveAttendance: (attendance: Attendance) => setDoc(doc(db, C.attendance, attendance.id), cleanData(attendance), { merge: true }),

  deleteAttendance: (attendanceId: string) => deleteDoc(doc(db, C.attendance, attendanceId)),

  // 休憩終了: breakTimeを加算し、breakStartTimeフィールドを削除する
  endBreak: async (record: Attendance, endTime: string): Promise<void> => {
    const breakStartMins = record.breakStartTime ? timeToMinutes(record.breakStartTime) : 0;
    const endMins = timeToMinutes(endTime);
    // 日付をまたぐ休憩（例：23:45〜00:15）も数える
    const breakDuration = endMins >= breakStartMins ? endMins - breakStartMins : endMins + 24 * 60 - breakStartMins;
    await updateDoc(doc(db, C.attendance, record.id), {
      status: 'working',
      breakTime: (record.breakTime || 0) + breakDuration,
      breakStartTime: deleteField(),
    });
  },

  saveAttendances: async (attendances: Attendance[]): Promise<void> => {
    const chunks = chunkArray(attendances, 450);
    for (const chunk of chunks) {
      const batch = writeBatch(db);
      chunk.forEach(a => batch.set(doc(db, C.attendance, a.id), cleanData(a), { merge: true }));
      await batch.commit();
    }
  },

  getStore: async (storeId: string): Promise<Store | null> => {
    if (!storeId) return null;
    const snap = await getDoc(doc(db, C.stores, storeId));
    return snap.exists() ? docToTypedSnap<Store>(snap) : null;
  },

  clearShiftPeriod: async (storeId: string): Promise<void> => {
    await updateDoc(doc(db, C.stores, storeId), { lastShiftPeriod: deleteField(), shiftCreationStatus: 'planning' });
  },

  // 店舗単位のシフト購読（シフト作成画面のリアルタイム更新用）
  subscribeStoreShifts: (storeId: string, range: { start: string; end: string }, callback: (shifts: Shift[]) => void): Unsubscribe =>
    onSnapshot(query(collection(db, C.shifts), where('store', '==', storeId), where('start', '>=', `${range.start}T00:00:00`), where('start', '<=', `${range.end}T23:59:59`)),
      snap => callback(snap.docs.map(d => docToTyped<Shift>(d))), e => console.error('subscribeStoreShifts', e)),

  // 管理しているすべての店舗のシフト購読（他店舗での勤務も、同日重複・勤務時間・連勤の判定に含める）
  subscribeAllShifts: (range: { start: string; end: string }, callback: (shifts: Shift[]) => void): Unsubscribe => {
    let unsubs: Unsubscribe[] = [];
    let cancelled = false;
    const byStore = new Map<string, Shift[]>();
    tenantApi.getStores().then(stores => {
      if (cancelled) return;
      unsubs = stores.map(st => onSnapshot(query(collection(db, C.shifts), where('store', '==', st.id), where('start', '>=', `${range.start}T00:00:00`), where('start', '<=', `${range.end}T23:59:59`)),
        snap => { byStore.set(st.id, snap.docs.map(d => docToTyped<Shift>(d))); callback(Array.from(byStore.values()).flat()); },
        e => console.error('subscribeAllShifts', e)));
    });
    return () => { cancelled = true; unsubs.forEach(u => u()); };
  },

  // スタッフ一覧のリアルタイム購読（招待の受諾など、別の端末での変更も反映する）
  subscribeStaff: (callback: () => void): Unsubscribe => {
    let unsub: Unsubscribe = () => {};
    let cancelled = false;
    let first = true;
    tenantApi.getStores().then(stores => {
      if (cancelled || stores.length === 0) return;
      unsub = onSnapshot(query(collection(db, C.staff), where('stores', 'array-contains-any', stores.slice(0, 30).map(s => s.id))), () => { if (first) { first = false; return; } callback(); });
    });
    return () => { cancelled = true; unsub(); };
  },

  // ---- 対応業務 ----
  getDuties: async (): Promise<Duty[]> => {
    const storeIds = (await tenantApi.getStores()).map(s => s.id);
    if (storeIds.length === 0) return [];
    const snaps = await Promise.all(chunkArray(storeIds, 30).map(ids => getDocs(query(collection(db, C.duties), where('storeIds', 'array-contains-any', ids)))));
    const map = new Map<string, Duty>();
    snaps.forEach(sn => sn.docs.forEach(d => map.set(d.id, docToTyped<Duty>(d))));
    return Array.from(map.values());
  },
  saveDuties: async (duties: Duty[]): Promise<void> => {
    const batch = writeBatch(db);
    duties.forEach(d => batch.set(doc(db, C.duties, d.id), cleanData(d), { merge: true }));
    await batch.commit();
  },
  deleteDuty: async (id: string): Promise<void> => {
    // セキュリティルール上、スタッフは自分の店舗で絞り込んで検索する
    const storeIds = (await tenantApi.getStores()).map(s => s.id);
    const snaps = storeIds.length ? await Promise.all(chunkArray(storeIds, 30).map(ids => getDocs(query(collection(db, C.staff), where('stores', 'array-contains-any', ids))))) : [];
    const holders = { docs: snaps.flatMap(sn => sn.docs).filter((d, i, arr) => ((d.data().dutyIds || []) as string[]).includes(id) && arr.findIndex(x => x.id === d.id) === i) };
    const batch = writeBatch(db);
    holders.docs.forEach(d => batch.update(d.ref, { dutyIds: ((d.data().dutyIds || []) as string[]).filter(x => x !== id) }));
    batch.delete(doc(db, C.duties, id));
    await batch.commit();
  },

  // ---- スタッフ同士の組み合わせルール（ペア・相性NG） ----
  getStaffRules: async (): Promise<StaffRule[]> => {
    const storeIds = (await tenantApi.getStores()).map(s => s.id);
    if (storeIds.length === 0) return [];
    // セキュリティルール上、店舗ごとに検索する（ルールは店舗の管理者のみ読める。読めない店舗は飛ばす）
    const snaps = (await Promise.all(storeIds.map(id => getDocs(query(collection(db, C.staff_rules), where('storeId', '==', id))).catch(() => null)))).filter((x): x is NonNullable<typeof x> => !!x);
    const map = new Map<string, StaffRule>();
    snaps.forEach(sn => sn.docs.forEach(d => map.set(d.id, docToTyped<StaffRule>(d))));
    return Array.from(map.values());
  },
  saveStaffRules: async (rules: StaffRule[]): Promise<void> => {
    const batch = writeBatch(db);
    rules.forEach(r => batch.set(doc(db, C.staff_rules, r.id), cleanData({ ...r, storeId: r.storeIds[0] })));
    await batch.commit();
  },
  deleteStaffRule: async (id: string): Promise<void> => { await deleteDoc(doc(db, C.staff_rules, id)); },

  // 招待URLの発行はサーバーで行う（推測できない乱数で作り、データベースにはハッシュ値だけを保存する）
  generateInviteToken: async (staffId: string): Promise<string> => {
    const r = await httpsCallable<{ staffId: string }, { token: string; role: string }>(functions, 'createInviteToken')({ staffId });
    const origin = window.location.origin;
    let path = window.location.pathname;
    if (path.endsWith('/index.html')) path = path.replace('/index.html', '/');
    if (!path.endsWith('/')) path += '/';
    const roleParam = (r.data.role === 'system_admin' || r.data.role === 'store_manager') ? 'admin' : 'staff';
    return `${origin}${path}#/invite?token=${encodeURIComponent(r.data.token)}&role=${roleParam}`;
  },

  sendInviteEmail: async (data: SendInviteEmailData) => {
    const r = await httpsCallable(functions, 'sendInviteEmail')(data);
    pushNotification({ channel: 'メール', title: '招待メールを送信しました。', detail: `宛先 ${data.staffName}さん（${data.email}）` });
    return r;
  },
  getStaffByToken: (token: string) => httpsCallable(functions, 'getStaffByToken')({ token }).then(r => r.data as Staff | null),

  // 招待URLの内容（スタッフと店舗、LINE運用かどうか）。未ログインでも読めるようCloud Functionsで取得する。
  getInviteContext: async (token: string): Promise<{ staff: Staff; store: Store | null; lineEnabled: boolean } | null> => {
    const r = await httpsCallable<{ token: string }, { staff: Staff; store: Store | null; lineEnabled: boolean } | null>(functions, 'getInviteContext')({ token });
    return r.data;
  },

  // LINE連携。連携後はそのスタッフとしてログインする（Cloud Functionsが発行するトークンを使う）。
  // LINEのアクセストークンを渡し、サーバー側でLINEに本人確認してから連携する。
  linkLineAccount: async (data: { token: string; accessToken: string }) => {
    const r = await httpsCallable<typeof data, { success: boolean; customToken?: string | null }>(functions, 'linkLineAccount')(data);
    if (r.data?.customToken && !auth.currentUser) await signInWithCustomToken(auth, r.data.customToken);
    return r;
  },

  // LINEからのログイン（連携済みのスタッフ）。LIFFのアクセストークンで本人確認する。
  lineSignIn: async (accessToken: string): Promise<boolean> => {
    const r = await httpsCallable<{ accessToken: string }, { customToken?: string }>(functions, 'lineSignIn')({ accessToken });
    if (!r.data?.customToken) return false;
    await signInWithCustomToken(auth, r.data.customToken);
    return true;
  },

  // LINEを使わない運用：招待URLからパスワードを設定してログインアカウントを作成する。
  acceptAccountInvite: async (token: string, password: string): Promise<Staff> => {
    const r = await httpsCallable<{ token: string; password: string }, { email: string; staff: Staff }>(functions, 'acceptAccountInvite')({ token, password });
    await signInWithEmailAndPassword(auth, r.data.email, password);
    return r.data.staff;
  },

  signIn: async (email: string, password: string): Promise<Staff | null> => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const me = await getDoc(doc(db, C.staff, cred.user.uid));
    return me.exists() ? normalizeStaffDoc(me) : null;
  },

  signOut: async (): Promise<void> => { await fbSignOut(auth); },
  sendPasswordReset: (email: string) => sendPasswordResetEmail(auth, email),

  createCheckoutSession: (storeId: string, priceId: string, quantity: number = 1) => httpsCallable<unknown, { url: string }>(functions, 'createStripeCheckoutSession')({
    storeId, priceId, quantity,
    successUrl: window.location.origin + '/#/admin/settings?payment=success',
    cancelUrl: window.location.origin + '/#/admin/settings?payment=cancel'
  }).then(r => r.data.url),

  createPortalSession: (storeId: string) => httpsCallable<unknown, { url: string }>(functions, 'createStripePortalSession')({
    storeId,
    returnUrl: window.location.origin + '/#/admin/settings'
  }).then(r => r.data.url),

  completeSetup: async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) return;
    await setDoc(doc(db, C.staff, user.uid), { role: 'system_admin', setupCompleted: true, updatedAt: new Date().toISOString() }, { merge: true });
  },

  geocodeAddress: async (address: string): Promise<{ lat: number; lng: number }> => {
    const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GEMINI_API_KEY });
    try {
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `日本国内の以下の住所の緯度と経度のみを返してください： "${address}"。
            結果は "lat" と "lng" キーを持つ有効なJSONオブジェクトとしてフォーマットしてください。
            住所が完全に無効な場合は、東京駅の座標を返してください： {"lat": 35.6812, "lng": 139.7671}。`,
        config: {
          responseMimeType: "application/json"
        }
      });
      return JSON.parse(response.text || '{"lat": 35.6812, "lng": 139.7671}');
    } catch (e) {
      console.error("Geocoding failed:", e);
      return { lat: 35.6812, lng: 139.7671 };
    }
  },

  generateDemoData: async (): Promise<boolean> => {
    const user = auth.currentUser;
    if (!user) throw new Error('Authentication required');
    const stores = await tenantApi.getStores();

    let store: Store;
    if (stores.length === 0) {
      store = { id: `store_${Date.now()}`, name: 'デモ店舗', companyName: 'デモ企業', ownerId: user.uid, plan: 'business', createdAt: new Date().toISOString(), submissionCycle: 'monthly', baseStartTime: '09:00', baseEndTime: '22:00', allowDigitalAbsence: true };
      await tenantApi.saveStores([store]);
    } else {
      store = stores[0];
    }

    const staffDocSnap = await getDoc(doc(db, C.staff, user.uid));
    let myStaffData: Staff;
    if (staffDocSnap.exists()) {
      const existing = normalizeStaffDoc(staffDocSnap);
      myStaffData = { ...existing, id: user.uid, stores: Array.from(new Set([...existing.stores, store.id])), role: 'system_admin', status: 'active', paidLeaveCount: existing.paidLeaveCount || 0 };
    } else {
      myStaffData = { id: user.uid, lastName: '管理者', firstName: 'ユーザー', email: user.email || '', hourlyWage: 1200, limit: 0, insuranceType: 'none', tags: ['管理者', 'デモ'], avatarColor: 'bg-slate-800', lineStatus: 'unconnected', stores: [store.id], isGuest: false, skills: [], positionIds: [], employmentType: 'full_time', role: 'system_admin', status: 'active', paidLeaveCount: 0 };
    }
    await tenantApi.saveStaff([myStaffData]);

    // デモデータ生成AI応答の型
    interface DemoGenPosition { name: string; color: string }
    interface DemoGenShiftType { name: string; time: string; abbr: string }
    interface DemoGenStaff { lastName: string; firstName: string; email: string; hourlyWage: number; employmentType: 'full_time' | 'part_time' | 'contract' }
    interface DemoGenData { positions: DemoGenPosition[]; shiftTypes: DemoGenShiftType[]; staff: DemoGenStaff[] }

    const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GEMINI_API_KEY });
    const responseSchema = { type: Type.OBJECT, properties: { positions: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { name: { type: Type.STRING }, color: { type: Type.STRING } }, required: ["name", "color"] } }, shiftTypes: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { name: { type: Type.STRING }, time: { type: Type.STRING }, abbr: { type: Type.STRING } }, required: ["name", "time", "abbr"] } }, staff: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { lastName: { type: Type.STRING }, firstName: { type: Type.STRING }, email: { type: Type.STRING }, hourlyWage: { type: Type.NUMBER }, employmentType: { type: Type.STRING } }, required: ["lastName", "firstName", "email", "hourlyWage", "employmentType"] } } }, required: ["positions", "shiftTypes", "staff"] };

    try {
      const response = await ai.models.generateContent({ model: 'gemini-3-flash-preview', contents: `Generate a realistic demo dataset for a SaaS shift management app. The store name is "${store.name}".`, config: { responseMimeType: "application/json", responseSchema: responseSchema } });
      const text = response.text;
      if (!text) throw new Error('Empty response from AI');
      const data: DemoGenData = JSON.parse(text);
      const positions: Position[] = (data.positions || []).map((p: DemoGenPosition) => ({ id: `pos_${Math.random().toString(36).substring(2, 11)}`, name: p.name, color: p.color || 'bg-blue-100 text-blue-800', storeIds: [store.id] }));
      await tenantApi.savePositions(positions);
      const shiftTypes: ShiftType[] = (data.shiftTypes || []).map((t: DemoGenShiftType) => ({ id: Date.now() + Math.floor(Math.random() * 1000), name: t.name, time: t.time, abbr: t.abbr, memo: "Demo data", storeIds: [store.id], storeId: store.id }));
      await tenantApi.saveShiftTypes(shiftTypes);
      const demoStaff: Staff[] = (data.staff || []).map((s: DemoGenStaff, idx: number) => {
        const staffPosIds = [positions[idx % positions.length].id];
        return { ...s, employmentType: (s.employmentType || 'full_time') as Staff['employmentType'], id: `demo_${Math.random().toString(36).substring(2, 11)}`, stores: [store.id], lineStatus: 'unconnected' as const, role: 'staff' as const, avatarColor: 'bg-blue-500', skills: staffPosIds.map(pid => ({ positionId: pid, level: 1 })), positionIds: staffPosIds, tags: ['デモ'], isGuest: false, limit: 1000, insuranceType: 'none', priority: 'medium' as const, status: 'active' as const, paidLeaveCount: 10 };
      });
      await tenantApi.saveStaff(demoStaff);

      const attendanceRecords: Attendance[] = [];
      const today = new Date();
      for (const staff of [...demoStaff, myStaffData]) {
        for (let i = 0; i < 30; i++) {
          const date = new Date();
          date.setDate(today.getDate() - i);
          const dateStr = getJSTDateString(date);
          if (Math.random() > 0.7) continue;
          const startHour = 9 + Math.floor(Math.random() * 3);
          const startMin = Math.random() > 0.5 ? 0 : 30;
          const startTime = `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`;
          const durationHours = 4 + Math.floor(Math.random() * 5);
          const endTime = `${String(startHour + durationHours).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`;
          const breakTime = durationHours >= 6 ? 60 : 0;
          attendanceRecords.push({ id: `demo_att_${staff.id}_${dateStr}`, staffId: staff.id, storeId: store.id, date: dateStr, startTime, endTime, breakTime, status: 'finished', note: 'デモデータ' });
        }
      }
      await tenantApi.saveAttendances(attendanceRecords);
      return true;
    } catch (e: unknown) {
      console.error("Gemini Error:", e);
      const errorMessage = e instanceof Error ? e.message : String(e);
      if (errorMessage.includes('429') || errorMessage.includes('quota')) {
        throw new Error('APIの利用制限に達しました。時間を置いてから再試行してください。');
      }
      throw e;
    }
  },

  clearStoreData: async (storeId: string): Promise<void> => {
    const collectionsToClear = [C.shifts, C.positions, C.shift_types, C.attendance, C.events, C.absences, C.shift_patterns, C.default_shift_settings];
    for (const colName of collectionsToClear) {
      const fieldName = colName === C.shifts ? 'store' : colName === C.positions ? 'storeIds' : 'storeId';
      let q;
      if (colName === 'positions') {
        q = query(collection(db, colName), where('storeIds', 'array-contains', storeId));
      } else {
        q = query(collection(db, colName), where(fieldName, '==', storeId));
      }
      const snap = await getDocs(q);
      const docChunks = chunkArray(snap.docs, 450);
      for (const chunk of docChunks) {
        const batch = writeBatch(db);
        chunk.forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    }
    const staffSnap = await getDocs(query(collection(db, C.staff), where('stores', 'array-contains', storeId)));
    const staffBatch = writeBatch(db);
    staffSnap.docs.forEach(d => {
      const stores = (d.data().stores || []).filter((s: string) => s !== storeId);
      staffBatch.update(d.ref, { stores });
    });
    if (staffSnap.docs.length > 0) await staffBatch.commit();
  },

  clearAllData: async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('Authentication required');
    const storesSnap = await getDocs(query(collection(db, C.stores), where('ownerId', '==', user.uid)));
    const storeIds = storesSnap.docs.map(d => d.id);
    if (storeIds.length === 0) return;
    const collectionsToClear = [C.shifts, C.positions, C.shift_types, C.attendance, C.events, C.absences, C.shift_patterns];
    for (const colName of collectionsToClear) {
      const fieldName = colName === C.shifts ? 'store' : 'storeId';
      const idChunks = chunkArray(storeIds, 10);
      for (const chunk of idChunks) {
        const q = query(collection(db, colName), where(fieldName, 'in', chunk));
        const snap = await getDocs(q);
        const docChunks = chunkArray(snap.docs, 5);
        for (const dChunk of docChunks) {
          const batch = writeBatch(db);
          dChunk.forEach(d => batch.delete(d.ref));
          await batch.commit();
        }
      }
    }
    const staffSnap = await getDocs(query(collection(db, C.staff), where('stores', 'array-contains-any', storeIds)));
    const staffChunks = chunkArray(staffSnap.docs, 5);
    for (const sChunk of staffChunks) {
      const staffBatch = writeBatch(db);
      sChunk.forEach(d => {
        if (d.id === user.uid) staffBatch.update(d.ref, { stores: [], setupCompleted: false });
        else staffBatch.delete(d.ref);
      });
      await staffBatch.commit();
    }
    const storesChunks = chunkArray(storesSnap.docs, 5);
    for (const stChunk of storesChunks) {
      const storeBatch = writeBatch(db);
      stChunk.forEach(d => storeBatch.delete(d.ref));
      await storeBatch.commit();
    }
  },

  checkStaffShiftOverlap: async (staffId: string, start: string, end: string, excludeId?: string): Promise<boolean> => {
    const dateStr = start.split('T')[0];
    // セキュリティルール上、他のスタッフのシフトは店舗ごとに検索する（自分が管理する店舗のみ）
    const stores = await tenantApi.getStores();
    const snaps = await Promise.all(stores.map(st => getDocs(query(collection(db, C.shifts), where('store', '==', st.id), where('staffId', '==', staffId), where('start', '>=', `${addDaysStr(dateStr, -1)}T00:00:00`), where('start', '<=', `${addDaysStr(dateStr, 1)}T23:59:59`)))));
    // 前日の夜から続くシフト・翌日の早い時間のシフト（深夜営業）とも重ならないか確認する
    const shifts = snaps.flatMap(sn => sn.docs.map(d => docToTyped<Shift>(d))).filter(s => s.id !== excludeId && (s.confirmed || s.type === 'fixed'));
    const newStart = new Date(start).getTime();
    const newEnd = new Date(end).getTime();
    return shifts.some(s => {
      const sStart = new Date(s.start).getTime();
      const sEnd = new Date(s.end).getTime();
      return (newStart < sEnd && newEnd > sStart);
    });
  }
};

export const api = {
  ...tenantApi,
  admin: {
    getStores: () => getDocs(collection(db, C.stores)).then(s => s.docs.map(d => docToTyped<Store>(d))),
    getStaff: () => getDocs(collection(db, C.staff)).then(s => s.docs.map(d => docToTyped<Staff>(d))),
  }
};
